import { http } from '@/utils/http'
import { useGameStatus } from '@/composables/useGameStatus'
import type { Page, PageResponse, PageRequest, Response } from '@/types/http'
import type {
  GameDTO,
  EntertainmentDTO,
  PlatformDTO,
  HotMatchDTO,
  BetRecordGameType,
  BetRecordDTO,
  BetRecordRequestBody,
  BetRecordSumDTO,
  GameStatusItem,
  GameElectronItem,
  GameLabel
} from '@/types/game'
import toast from '@/utils/toast'
import dialog from '@/utils/dialog'
import i18n from '@/i18n'

/**
 * 游戏相关类型定义
 */
export interface LaunchGameParams {
  gameName: string
  gameId: number | string
  platformCode: string | number
  gameType: number | string
  isDark?: boolean
  notSupportIframe?: boolean
  extra?: {
    gameCode?: string
    aliasCode?: string
  }
}

export interface LaunchGameResponse {
  url: string
  adaption: number
  newUrl?: string
  [key: string]: any
}

export interface GameAnalysisParams {
  gameName: string
  platformCode: string | number
  gameType: number | string
  gameId: number | string
  username: string
  userId: string | number
  type: number
}

export interface ExitGameParams {
  gameType: number | string
  platformCode: string | number
}

/**
 * 启动游戏
 * @param params 游戏参数
 */
export async function launchGame(params: LaunchGameParams): Promise<LaunchGameResponse> {
  toast.loading(i18n.global.t('正在启动游戏，请稍候'))
  try {
    // 添加设备类型参数
    const gameParams = {
      ...params,
      type: 'PC' // PC端固定为PC类型
    }

    const response = await http<{ data: LaunchGameResponse }>('/app-api/game/startGame', {
      method: 'POST',
      body: gameParams,
      showLoading: true,
      needLogin: true
    })

    const result = response.data

    // 处理游戏启动结果
    if (params.notSupportIframe && !result?.url?.startsWith('http')) {
      console.error('游戏地址非http协议', result?.url)
    }

    if (params.notSupportIframe && result?.url?.startsWith('http')) {
      window.open(result?.url, '_blank')
      return { url: '', adaption: 0 }
    }

    return result
  } catch (error: any) {
    console.error('启动游戏失败:', error)
    dialog.alert({
      title: i18n.global.t('温馨提示'),
      content: error.msg
    })
    throw error
  } finally {
    toast.clear()
  }
}

/**
 * 退出游戏
 */
export async function exitGame(params: ExitGameParams): Promise<void> {
  try {
    await http('/app-api/game/exit', {
      method: 'POST',
      body: params
    })
  } catch (error) {
    console.error('退出游戏失败:', error)
  }
}

/**
 * 热门游戏
 */
export async function getHotGames() {
  return http<PageResponse<GameDTO>>('/app-api/game/hotGame', {
    method: 'POST'
  })
}

/**
 * 首页场馆列表
 */
export async function getEntertainment() {
  return http<Response<EntertainmentDTO[]>>('/app-api/game/gameTypePlatformList', {
    method: 'POST'
  })
}

/**
 * 获取平台游戏列表
 */
export async function getGamesByPlatform(params: {
  platformCode: string
  gameType: number
  page?: number
  size?: number
}) {
  return http<PageResponse<GameDTO>>('/app-api/game/gameListByPlatform', {
    method: 'POST',
    body: {
      platformCode: params.platformCode,
      gameType: params.gameType,
      pageNum: params.page || 1,
      pageSize: params.size || 20
    }
  })
}

/**
 * 获取热门足球赛事
 */
export async function getSportHotMatch() {
  return http<Response<HotMatchDTO[]>>('/app-api/game/sport/hot/matchList', {
    method: 'POST'
  })
}

/**
 * 获取游戏类型
 */
export async function getGameTypesList() {
  return await http<Response<BetRecordGameType[]>>('/app-api/game/init/getTypeList', {
    method: 'POST'
  })
}

/**
 * 获取所有平台列表
 */
export async function getAllPlatforms(gameType: number | string) {
  const res = await http<Response<PlatformDTO[]>>('/app-api/game/getAllPlatformList')
  return {
    code: 200,
    data: res.data?.filter((it) => it.gameType === Number(gameType) && it.enable === true) || []
  }
}

/**
 * 获取游戏列表（支持筛选和分页）
 * @deprecated 建议使用 useGameStatus 的 queryGameList 方法
 */
export async function getGames(
  params: PageRequest<{
    gameType: number | string
    platformCode?: string
    gameLabel?: GameLabel
  }> = {}
) {
  const { queryGameList } = useGameStatus()
  return queryGameList(params)
}

/**
 * 获取所有游戏
 */
export const getAllGames = async (): Promise<GameElectronItem[]> => {
  const { data = [] } = await http<Response<GameElectronItem[]>>('/app-api/game/totalGameElectron')
  return data
}

export const getGameStatus = async (): Promise<GameStatusItem[]> => {
  const { data = [] } = await http<Response<GameStatusItem[]>>('/app-api/game/totalGameStatus')
  return data
}

// 收藏游戏
export function collectGame(id: number) {
  return http<Response<{ isCollect: boolean }>>('/app-api/game/userCollectGame', {
    method: 'POST',
    body: { gameId: id },
    needLogin: true
  })
}

// 获取我的收藏
export async function getMyCollection(params: PageRequest = {}) {
  return http<PageResponse<GameDTO>>('/app-api/game/myCollectGame', {
    method: 'POST',
    body: params,
    needLogin: true
  })
}

// 注单列表和统计聚合
export async function queryTicketEgg(body: {
  ticketPage: PageRequest<BetRecordRequestBody>
  ticketSum: BetRecordRequestBody
}) {
  body.ticketPage.model!.timeType = 1
  body.ticketSum.timeType = 1
  delete body.ticketSum.lastId
  return await http<
    Response<{
      ticketPage: Page<BetRecordDTO>
      ticketSum: BetRecordSumDTO
    }>
  >('/app-api/member/gameticket/agg', {
    method: 'POST',
    body
  })
}

// 投注列表查询 (PC没有注单，所以用这个接口，统计聚合的接口很慢)
export async function ticketPageQuery(data: PageRequest<BetRecordRequestBody> = {}) {
  return await http<PageResponse<BetRecordDTO>>('/app-api/game/ticket/page', {
    method: 'POST',
    body: data
  }).then((res) => {
    return res
  })
}
